import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { formatInstant } from '../../lib/calendar-date';
import { classifyLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import {
  type Checklist, type LastPostTrip, CERTIFICATION_LABEL, personName,
} from '../../lib/trip-inspections';

/**
 * One unit's last post-trip, shown on a pre-trip for the driver to review and
 * acknowledge (49 CFR 396.13(b)–(c); api 0437).
 *
 * FOUR STATES, NOT TWO. Loading; failed (a notice, and the form refuses to
 * file — a review that did not load cannot be acknowledged); `null` from the
 * API, which is a fact about the unit ("no post-trip on record") and needs no
 * acknowledgement; and a report, which does — every time, defects or none.
 *
 * The acknowledgement is bound to the post-trip's id, not to the unit: if a
 * newer post-trip arrives (the server's 409 refreshes the query), the id
 * changes, the box reads unticked again, and the driver reviews the new one.
 *
 * An uncertified MAJOR defect is said in words: the unit stays out of service
 * until the office certifies the repair. The pre-trip does not change that.
 */
export default function LastPostTripReview({
  unitNumber, state, checklist, timeZone, acknowledged, onAcknowledge,
}: {
  unitNumber: string;
  state: { data: LastPostTrip | null | undefined; error: unknown; isLoading: boolean };
  checklist: Checklist;
  timeZone: string | null;
  acknowledged: boolean;
  onAcknowledge: (on: boolean) => void;
}) {
  const sev = (s: 'minor' | 'major') => checklist.severity_labels[s] ?? s;
  const itemLabel = (key: string) => checklist.items.find((i) => i.key === key)?.label ?? key;
  const r = state.data;

  return (
    <div data-testid={`last-post-trip-${unitNumber}`} className="rounded-lg border border-fieldiq-border bg-fieldiq-dark p-3">
      <p className="text-sm font-semibold text-fieldiq-text">Last post-trip · {unitNumber}</p>
      {state.isLoading ? (
        <p className="mt-2 flex items-center gap-2 text-sm text-fieldiq-muted"><Loader2 className="size-4 animate-spin" /> Loading the last post-trip…</p>
      ) : state.error ? (
        <div className="mt-2">
          <LoadFailureNotice error={state.error} kind={classifyLoadFailure(state.error) ?? 'unreachable'} subject={`the last post-trip for ${unitNumber}`} />
        </div>
      ) : r === null ? (
        <p className="mt-2 text-sm text-fieldiq-text">No post-trip on record for {unitNumber}. Nothing to review.</p>
      ) : r ? (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-fieldiq-muted">
            {formatInstant(r.inspected_at, timeZone) ?? '—'} · {personName(r.driver_first_name, r.driver_last_name)} · {r.location || '—'}
          </p>
          {r.defects.length === 0 ? (
            <p className="text-sm text-fieldiq-text">{r.no_defects ? 'No defects reported.' : `No defects reported on ${unitNumber}.`}</p>
          ) : (
            <ul className="space-y-2">
              {r.defects.map((d) => {
                const openMajor = d.severity === 'major' && !d.certification;
                return (
                  <li key={d.id} className="rounded-lg border border-fieldiq-border bg-fieldiq-card p-2 text-sm text-fieldiq-text">
                    <p>
                      <span className="font-semibold">{sev(d.severity)}</span> · {itemLabel(d.item_key)}: {d.defect_text}
                      {d.notes ? <span className="text-fieldiq-muted"> ({d.notes})</span> : null}
                    </p>
                    <p className="mt-1 text-xs text-fieldiq-muted">
                      {d.certification
                        ? `Certified: ${CERTIFICATION_LABEL[d.certification] ?? '—'} · ${personName(d.certified_by_first_name, d.certified_by_last_name)} · ${formatInstant(d.certified_at, timeZone) ?? '—'}`
                        : 'Not certified'}
                    </p>
                    {openMajor && (
                      <p role="note" className="mt-1 flex items-start gap-1.5 text-xs font-semibold text-fieldiq-text">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                        {unitNumber} stays out of service until the office certifies this repair.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <label className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-fieldiq-text">
            <input type="checkbox" checked={acknowledged} onChange={(e) => onAcknowledge(e.target.checked)} />
            I have reviewed {unitNumber}'s last post-trip report
          </label>
        </div>
      ) : null}
    </div>
  );
}
